/** A project's LLM service choice.
 *
 *  Beta-user-only on the server: anyone else sending a service gets a 403 on
 *  create and on PUT. The UI hides the control for everyone else, so reaching
 *  either error means the check was bypassed.
 *
 *  See docs/multi-llm.md — a project stores `llm.service`, a service id
 *  or null for "use the platform default".
 */
import api from '../utils/axios';

/** One service this deployment can actually serve, from GET /llm/options. */
export interface LlmServiceOption {
  id: string;
  label: string;
  channel: string;
  channel_label: string;
}

export interface LlmOptions {
  /** The "use the platform default" entry. Its `id` is always null. */
  default: {
    id: null;
    /** Already formatted by the server, e.g. "Default (Claude)". */
    label: string;
    /** The service the default resolves to right now. */
    service: string;
  };
  services: LlmServiceOption[];
}

/** A project's stored choice, plus what it means on this deployment now. */
export interface ProjectLlmSetting {
  /** The stored choice: a service id, or null for the platform default. */
  service: string | null;
  effective_service: string;
  service_from: 'project' | 'platform';
  /** False when the stored service is no longer offered here. */
  available: boolean;
  /** Why it cannot be used, when `available` is false. */
  problem: string | null;
  /** Every tier this project's channel serves, low to high. */
  tiers: ('low' | 'medium' | 'high')[];
  /**
   * The tiers to offer in the Medium/High control, already intersected with
   * what the channel can actually serve. Empty means show no control: the
   * feature has none, or the channel serves only one of them.
   *
   * Never hard-code the options. A channel that has Medium but not High —
   * GLM on a Bedrock account without GLM-5, for instance — would otherwise
   * show a High button that silently resolves to Medium.
   */
  picks: ('medium' | 'high')[];
  updated_by?: string;
  updated_at?: number;
}

/** What a beta user may choose from on this deployment. */
export async function getLlmOptions(): Promise<LlmOptions> {
  const response = await api.get('/llm/options');
  return response.data;
}

export async function getProjectLlm(projectId: string): Promise<ProjectLlmSetting> {
  const response = await api.get(`/projects/${projectId}/llm`);
  return response.data;
}

/** Save a project's service. `null` means "use the platform default". */
export async function setProjectLlm(
  projectId: string,
  service: string | null
): Promise<ProjectLlmSetting> {
  const response = await api.put(`/projects/${projectId}/llm`, { service });
  return response.data;
}

/**
 * The tier options to show for a project, or `null` while unknown.
 *
 * Falls back to showing every option on failure: a picker that renders
 * nothing because a request failed is worse than one offering a tier the
 * server will correct downward (the resolver degrades a tier the channel
 * cannot serve, recording the reason).
 */
export async function getProjectTierPicks(
  projectId: string
): Promise<('medium' | 'high')[]> {
  const setting = await getProjectLlm(projectId);
  return setting.picks ?? [];
}
